import type { GradeSheetStudent, HomeworkCounts } from '../types';

// Các cột BTVN theo thứ tự hiển thị: đúng / đã làm / được giao.
export const HOMEWORK_FIELDS = ['correctExercises', 'completedExercises', 'assignedExercises'] as const;
export type HomeworkField = typeof HOMEWORK_FIELDS[number];
export type HomeworkDraft = Record<HomeworkField, string>;

export const EMPTY_HOMEWORK: HomeworkDraft = { correctExercises: '', completedExercises: '', assignedExercises: '' };

// '' → null, '7,5' → 7.5, chữ → NaN.
export function parseNumber(text: string): number | null {
    const trimmed = text.trim().replace(',', '.');
    if (trimmed === '') return null;
    return /^\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : Number.NaN;
}

export function formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    return String(Math.round(value * 100) / 100);
}

// Vắng, có phép hoặc chưa điểm danh thì khoá hàng cho tới khi được bật lại.
export function isLockedByDefault(student: Pick<GradeSheetStudent, 'status'>) {
    return student.status === null || student.status === 'absent' || student.status === 'excused';
}

export function homeworkDraftFrom(homework: HomeworkCounts | null): HomeworkDraft {
    if (!homework) return { ...EMPTY_HOMEWORK };
    return {
        correctExercises: formatNumber(homework.correctExercises),
        completedExercises: formatNumber(homework.completedExercises),
        assignedExercises: formatNumber(homework.assignedExercises),
    };
}

export type HomeworkDraftResult =
    | { kind: 'empty' }
    | { kind: 'ok'; homework: HomeworkCounts }
    | { kind: 'error'; message: string };

export function readHomeworkDraft(draft: HomeworkDraft): HomeworkDraftResult {
    const values = HOMEWORK_FIELDS.map(field => parseNumber(draft[field]));
    if (values.every(value => value === null)) return { kind: 'empty' };
    if (values.some(value => value === null)) return { kind: 'error', message: 'Nhập đủ cả 3 ô' };
    if (values.some(value => Number.isNaN(value))) return { kind: 'error', message: 'Chỉ nhập số' };
    const [correct, completed, assigned] = values as number[];
    if (correct > completed) return { kind: 'error', message: 'Số bài đúng lớn hơn số bài đã làm' };
    if (completed > assigned) return { kind: 'error', message: 'Số bài đã làm lớn hơn số bài được giao' };
    return { kind: 'ok', homework: { correctExercises: correct, completedExercises: completed, assignedExercises: assigned } };
}

export function readScore(text: string): { kind: 'empty' } | { kind: 'ok'; score: number } | { kind: 'error'; message: string } {
    const value = parseNumber(text);
    if (value === null) return { kind: 'empty' };
    if (Number.isNaN(value) || value > 10) return { kind: 'error', message: 'Điểm từ 0 đến 10' };
    return { kind: 'ok', score: value };
}

// Áp một giá trị cho cả cột, bỏ qua các hàng đang bị khoá.
export function applyToColumn<T extends Record<string, string>>(
    drafts: Record<string, T>,
    unlockedIds: readonly string[],
    field: keyof T,
    value: string,
): Record<string, T> {
    const next = { ...drafts };
    for (const id of unlockedIds) {
        next[id] = { ...next[id], [field]: value };
    }
    return next;
}
